import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Calendar,
  Users,
  Bed,
  MapPin,
  Star,
  Download,
} from "lucide-react";

const BookingConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingDetails, hotel, room } = location.state || {};

  if (!bookingDetails || !hotel || !room) {
    navigate("/");
    return null;
  }

  const nights = Math.ceil(
    (bookingDetails.checkOut.getTime() - bookingDetails.checkIn.getTime()) /
      (1000 * 60 * 60 * 24)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-lg text-gray-600">
              Your reservation has been successfully created
            </p>
          </div>

          {/* Booking Details Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Booking Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hotel Info */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">
                  Hotel Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{hotel.name}</span>
                  </div>
                  <div className="text-sm text-gray-600 ml-6">
                    {hotel.location}
                  </div>
                  <div className="flex items-center space-x-1 ml-6">
                    {Array.from({ length: hotel.stars }, (_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Room Info */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Room Details</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Bed className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{room.type}</span>
                  </div>
                  <div className="text-sm text-gray-600 ml-6">
                    {room.bedType}
                  </div>
                  <div className="flex items-center space-x-2 ml-6">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Up to {room.maxOccupancy} guests
                    </span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">
                  Stay Duration
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">
                      {nights} night{nights !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 ml-6">
                    Check-in: {bookingDetails.checkIn.toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-600 ml-6">
                    Check-out: {bookingDetails.checkOut.toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Guest Info */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">
                  Guest Information
                </h3>
                <div className="space-y-1">
                  <div className="text-gray-700">
                    {bookingDetails.guestInfo.firstName}{" "}
                    {bookingDetails.guestInfo.lastName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {bookingDetails.guestInfo.email}
                  </div>
                  <div className="text-sm text-gray-600">
                    {bookingDetails.guestInfo.phone}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Price Summary
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>
                  Room rate ({nights} night{nights !== 1 ? "s" : ""})
                </span>
                <span>${room.price * nights}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Taxes & fees</span>
                <span>${Math.round(room.price * nights * 0.15)}</span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between text-lg font-semibold text-gray-900">
                  <span>Total</span>
                  <span>${bookingDetails.totalPrice}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">
              Important Information
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• You will receive a confirmation email shortly</li>
              <li>• Check-in time: 3:00 PM</li>
              <li>• Check-out time: 11:00 AM</li>
              <li>• Payment will be collected at the hotel</li>
              <li>• Free cancellation until 24 hours before check-in</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.print()}
              className="btn btn-secondary flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Confirmation</span>
            </button>
            <button onClick={() => navigate("/")} className="btn btn-primary">
              Book Another Hotel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
